/**
 * The assistant's name was Coach and is OS now (web redesign stage 1, Decided
 * 16); the code keeps the old word, and both names are held here, because the
 * server's prompts and older builds still say Coach.
 *
 * To the person there is one assistant, Coach, in two modes: voice mode and
 * chat mode (FD7). Whether a line the person hears or reads frames Coach as a
 * second party — someone the speaker asks, waits for, hands to or quotes. The
 * name alone is not a frame: "I'm Coach", "Coach's voice", "What Coach knows".
 *
 * The catalogue writes its apostrophes as ’ (U+2019), so every apostrophe in a
 * pattern is `['’]`.
 */
const OTHER_COACH: readonly RegExp[] = [
  /\bcoach\s+(says|said|found|finds|is|was|will|takes|took|answered|answers|drew|draws|puts?|has|had|can['’]t|cannot|couldn['’]t|could|did|didn['’]t|does|doesn['’]t|thinks|looks|looked|checks|checked|hit|writes|wrote)\b/i,
  /\bcoach['’]s\s+(answers?|repl(y|ies)|turns?|work|steps?|words)\b/i,
  /\b(ask|asks|asking|asked|reach|reached|reaching|pass|passed|passing|hand|handed|handing|check with|checking with|from|to|with)\s+(it\s+|this\s+|that\s+)?(on\s+|over\s+)?(to\s+)?coach\b/i,
  /(coach|कोच)\s*(से|को|ने)\s/iu,
  /(coach|कोच)\s+\S+\s+(रही|रहा|रहे|रहीं)\s+(है|हैं|थी|था)/iu,
  /(coach|कोच)\s+(पाहते|पाहतो|જોઈ|ਦੇਖ)/iu,
  // A go-between that says "I" and names Coach in one breath: "I'm the voice
  // assistant for Coach", "on behalf of Coach", "Coach की ओर से".
  /\b(for|alongside|on behalf of|behind)\s+coach\b/i,
  /(coach|कोच)\s+(की ओर से|के लिए)/iu,
];

/**
 * The same frames around OS. The name is matched in capitals only, since "os"
 * is no English word, and never inside a word (RecordOS). "Ask OS" is not a
 * frame: it is the door's own name (the floating button, the palette), so the
 * asking verbs are left out of the third pattern.
 */
const OS = '(?<![A-Za-z])OS(?![A-Za-z])';
const OTHER_OS: readonly RegExp[] = [
  new RegExp(`${OS}\\s+(says|said|found|finds|is|was|will|takes|took|answered|answers|drew|draws|puts?|has|had|can['’]t|cannot|couldn['’]t|could|did|didn['’]t|does|doesn['’]t|thinks|looks|looked|checks|checked|hit|writes|wrote)\\b`),
  new RegExp(`${OS}['’]s\\s+(answers?|repl(y|ies)|turns?|work|steps?|words)\\b`),
  new RegExp(`\\b([Rr]each|reached|reaching|[Pp]ass|[Pp]assed|passing|[Hh]and|[Hh]anded|handing|check with|checking with|from|to|with)\\s+(it\\s+|this\\s+|that\\s+)?(on\\s+|over\\s+)?(to\\s+)?${OS}`),
  new RegExp(`${OS}\\s*(से|को|ने)\\s`, 'u'),
  new RegExp(`${OS}\\s+\\S+\\s+(रही|रहा|रहे|रहीं)\\s+(है|हैं|थी|था)`, 'u'),
  new RegExp(`${OS}\\s+(पाहते|पाहतो|જોઈ|ਦੇਖ)`, 'u'),
  new RegExp(`\\b([Ff]or|[Aa]longside|on behalf of|behind)\\s+${OS}`),
  new RegExp(`${OS}\\s+(की ओर से|के लिए)`, 'u'),
];

export const framesCoachAsOther = (text: string): boolean =>
  OTHER_COACH.some((pattern) => pattern.test(text)) || OTHER_OS.some((pattern) => pattern.test(text));

/**
 * Whether the voice says who it is as itself, the answer FD7 and the live
 * prefix's rule 5 name: "I'm Coach", "I am Coach" or "My name is Coach", or as
 * Hindi says it ("मैं Coach हूँ", "मैं कोच हूँ", "मैं Coach बोल रही हूँ"). The
 * name comes straight after the "I", so "I'm the voice that works alongside
 * Coach", "I'm not Coach" and "I'm Coach's voice" are not it, and neither is
 * "You are speaking with Coach." (Phase 11's smoke heard that twice) or an
 * answer that never says who it is. Read it beside `framesCoachAsOther`.
 */
const I_AM_COACH = /\b(?:I['’]?m|I am|my name is)\s+(?:Coach|OS)\b(?!['’]s\b)|मैं\s+(?:Coach|कोच|OS)\s+(?:हूँ|हूं|बोल\s*(?:रही|रहा)\s*(?:हूँ|हूं))/iu;

export const saysIAmCoach = (text: string): boolean => I_AM_COACH.test(text);
